import React, { useCallback, useEffect, useState } from 'react';
import Pagination from './Pagination';
import { useConfirm } from '../context/ConfirmContext';
import { cancelBooking, listAdminBookings } from '../api/visitBookingApi';

// ── Shared styles (mirrors GalleryManagement's local style consts) ─────────
const btnDanger = { padding: '6px 12px', background: '#fff', color: '#B91C1C', border: '1.5px solid #FCA5A5', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.02em', borderBottom: '1.5px solid #E5E7EB' };
const td = { padding: '10px 12px', fontSize: 13, color: '#374151', borderBottom: '1px solid #F3F4F6' };

const formatTime12h = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

export default function VisitBookingsManagement() {
  const confirm = useConfirm();
  const [bookings, setBookings] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadBookings = useCallback(() => {
    setLoading(true);
    listAdminBookings(page, 20)
      .then(r => { setBookings(r.data.content || []); setTotalPages(r.data.totalPages || 0); })
      .catch(() => { setBookings([]); setTotalPages(0); })
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { loadBookings(); }, [loadBookings]);

  const handleCancel = async (booking) => {
    const ok = await confirm({
      title: `Cancel ${booking.requestorName}'s visit?`,
      message: `Booked for ${booking.visitDate}.`,
      confirmLabel: 'Cancel visit',
      cancelLabel: 'Keep booking',
      tone: 'danger',
    });
    if (!ok) return;
    await cancelBooking(booking.id);
    loadBookings();
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 4px', letterSpacing: '-0.5px' }}>Visit Bookings</h1>
        <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>
          Walk-in visit requests for the Hyderabad experience center, booked from the public gallery.
        </p>
      </div>

      {loading ? (
        <p style={{ color: '#9CA3AF' }}>Loading…</p>
      ) : bookings.length === 0 ? (
        <p style={{ color: '#9CA3AF' }}>No bookings yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Date</th>
                <th style={th}>Time</th>
                <th style={th}>Name</th>
                <th style={th}>Company</th>
                <th style={th}>Email</th>
                <th style={th}>Phone</th>
                <th style={th}>Status</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {bookings.map(b => (
                <tr key={b.id}>
                  <td style={td}>{b.visitDate}</td>
                  <td style={td}>{formatTime12h(b.slotStartTime)}</td>
                  <td style={td}>{b.requestorName}</td>
                  <td style={td}>{b.companyName}</td>
                  <td style={td}>{b.companyEmail}</td>
                  <td style={td}>{b.phone}</td>
                  <td style={td}>
                    {b.cancelled
                      ? <span style={{ color: '#B91C1C', fontWeight: 600 }}>Cancelled</span>
                      : <span style={{ color: '#15803D', fontWeight: 600 }}>Confirmed</span>}
                  </td>
                  <td style={td}>
                    {!b.cancelled && <button style={btnDanger} onClick={() => handleCancel(b)}>Cancel</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
    </div>
  );
}
